import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { StatusChip } from '../../components/common/Chips'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import { dashboardApi } from '../../api/dashboardApi'



const mockDetailedReports = Array.from({ length: 10 }, (_, i) => ({
  id: i + 1,
  month: 'January 2024',
  generatedBy: 'Admin User',
  totalEmployees: 245,
  avgAttendance: 92.5,
  totalOvertime: '450 hours',
  status: i % 3 === 0 ? 'pending' : 'completed',
  downloadUrl: '#',
}))

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']

const AttendanceSummary = () => {
  const navigate = useNavigate()
  const [tabValue, setTabValue] = useState(0)
  const [reportType, setReportType] = useState('monthly')
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const [selectedDepartment, setSelectedDepartment] = useState('all')
  const [departmentData, setDepartmentData] = useState([])
  const [statsData, setStatsData] = useState({
    overall_attendance: 0,
    present: 0,
    absent: 0,
    late: 0,
    total_overtime: 0,
    improvement: 0
  })
  const [monthlyChartData, setMonthlyChartData] = useState([])
  const [loading, setLoading] = useState(false)


  const fetchDepartmentStats = async () => {
    try {
      setLoading(true)
      const response = await dashboardApi.getDepartmentAttendanceSummary()
      if (response.data) {
        const transformedData = Object.entries(response.data).map(([deptName, stats]) => ({
          department: deptName,
          attendance: stats.attendance_percent || 0,
          employees: stats.total_employees || 0
        }))
        setDepartmentData(transformedData)
      }
    } catch (error) {
      console.error('Error fetching department stats:', error)
    } finally {
      setLoading(false)
    }
  }


  const fetchStats = async () => {
    try {

      const filterMap = {
        'monthly': 'month',
        'weekly': 'week',
        'daily': 'today'
      }
      const filterType = filterMap[reportType] || 'month'

      const response = await dashboardApi.getAttendanceOverview({ filter_type: filterType })
      if (response.data) {
        setStatsData(response.data)
      }
    } catch (error) {
      console.error('Error fetching attendance overview:', error)
    }
  }


  const fetchMonthlyChart = async () => {
    try {
      const response = await dashboardApi.getMonthAttendanceChart()
      if (response.data) {



        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

        const transformedData = Object.entries(response.data)
          .map(([month, data]) => ({
            month,
            present: data.present || 0,
            absent: data.absent || 0,
            late: data.late || 0
          }))
          .sort((a, b) => monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month))

        setMonthlyChartData(transformedData)
      }
    } catch (error) {
      console.error('Error fetching monthly chart data:', error)
    }
  }

  useEffect(() => {
    if (tabValue === 0) {
      fetchDepartmentStats()
      fetchStats()
      fetchMonthlyChart()
    }
  }, [tabValue, reportType])


  React.useEffect(() => {

  }, [reportType, selectedDepartment, selectedMonth])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }


  const pieData = [
    { name: 'On Time', value: Math.max(0, (statsData.present || 0) - (statsData.late || 0)) },
    { name: 'Late', value: statsData.late || 0 },
    { name: 'Absent', value: statsData.absent || 0 },
  ]


  const CUSTOM_COLORS = ['#2e7d32', '#ed6c02', '#d32f2f']

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        <Box sx={{ mb: 4 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" fontWeight="bold">
                Attendance Reports & Analytics
              </Typography>
              <Typography variant="body2" color="textSecondary">
                View detailed attendance reports and analytics
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
              >
                Print Report
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
              >
                Export PDF
              </Button>
            </Box>
          </Box>

          <Paper sx={{ mb: 3 }}>

            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Dashboard" />
              <Tab label="Detailed Reports" />

              <Tab label="Custom Report" />
            </Tabs>
          </Paper>
        </Box>

        {
          tabValue === 0 && (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>

                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 3 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        views={['year', 'month']}
                        label="Select Month"
                        value={selectedMonth}
                        onChange={(newValue) => setSelectedMonth(newValue)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Report Type</InputLabel>
                      <Select
                        label="Report Type"
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="custom">Custom Range</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Department</InputLabel>
                      <Select
                        label="Department"
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                      >
                        <MenuItem value="all">All Departments</MenuItem>
                        <MenuItem value="IT">IT</MenuItem>
                        <MenuItem value="HR">HR</MenuItem>
                        <MenuItem value="Sales">Sales</MenuItem>
                        <MenuItem value="Production">Production</MenuItem>
                        <MenuItem value="Finance">Finance</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<FilterIcon />}
                    >
                      Generate Report
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              <Grid container spacing={3} sx={{ mb: 4 }}>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                        <Box>
                          <Typography variant="h4" fontWeight="bold">
                            {statsData.overall_attendance}%
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Overall Attendance
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid >

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <WarningIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                        <Box>
                          <Typography variant="h4" fontWeight="bold">
                            {statsData.late}%
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Late Attendance
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AccessTimeIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                        <Box>
                          <Typography variant="h4" fontWeight="bold">
                            {statsData.total_overtime}h
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Total Overtime
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TrendingUpIcon color="secondary" sx={{ fontSize: 40, mr: 2 }} />
                        <Box>
                          <Typography variant="h4" fontWeight="bold">
                            {statsData.improvement > 0 ? '+' : ''}{statsData.improvement}%
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            vs Last Period
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid >

              <Grid container spacing={3}>

                <Grid size={{ xs: 12, md: 8 }} sx={{ mb: 3 }}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Monthly Attendance Trend
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={monthlyChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="present" stroke="#2e7d32" strokeWidth={2} />
                          <Line type="monotone" dataKey="absent" stroke="#dc004e" strokeWidth={2} />
                          <Line type="monotone" dataKey="late" stroke="#ed6c02" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid >

                <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 3 }}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Attendance Distribution
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CUSTOM_COLORS[index % CUSTOM_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Department-wise Performance
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={departmentData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="department" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="attendance" fill="#1976d2" name="Attendance %" />
                          <Bar dataKey="employees" fill="#9c27b0" name="Employees" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>
              </Grid >
            </>
          )}

        {
          tabValue === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                Generated Reports
              </Typography>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Report Month</TableCell>
                      <TableCell>Generated By</TableCell>
                      <TableCell>Total Employees</TableCell>
                      <TableCell>Avg. Attendance</TableCell>
                      <TableCell>Total Overtime</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableSkeleton columns={7} rows={5} />
                    ) : (
                      mockDetailedReports.map((report) => (
                        <TableRow key={report.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon fontSize="small" color="action" />
                              {report.month}
                            </Box>
                          </TableCell>
                          <TableCell>{report.generatedBy}</TableCell>
                          <TableCell>{report.totalEmployees}</TableCell>
                          <TableCell>{report.avgAttendance}%</TableCell>
                          <TableCell>{report.totalOvertime}</TableCell>
                          <TableCell>
                            <StatusChip status={report.status} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="primary">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="default">
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )
        }



        {
          tabValue === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                Generate Custom Report
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={selectedMonth}
                      onChange={(newValue) => setSelectedMonth(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={selectedMonth}
                      onChange={(newValue) => setSelectedMonth(newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Report Format</InputLabel>
                    <Select label="Report Format" defaultValue="pdf">
                      <MenuItem value="pdf">PDF</MenuItem>
                      <MenuItem value="excel">Excel</MenuItem>
                      <MenuItem value="csv">CSV</MenuItem>
                      <MenuItem value="html">HTML</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button variant="contained" fullWidth size="large">
                    Generate Custom Report
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )
        }
      </Paper>
    </Box>
  )
}

export default AttendanceSummary